import { useState, FormEvent } from 'react';

export default function Contact() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.message) {
      setSubmitStatus('error');
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      formBody.append('name', formData.name);
      formBody.append('email', formData.email);
      formBody.append('message', formData.message);

      const response = await fetch('https://readdy.ai/api/form/d6jhpe2paf6ievb71g10', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ name: '', email: '', message: '' });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-16 lg:py-20 bg-white">
      <div className="max-w-[1100px] mx-auto px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16">
          {/* Left Column - Form */}
          <div>
            <h2 className="text-3xl lg:text-[32px] font-semibold text-[#111111]">
              Get in Touch
            </h2>
            
            <form 
              id="contact-form" 
              data-readdy-form 
              onSubmit={handleSubmit} 
              className="mt-8 space-y-5"
            >
              <div>
                <input
                  type="text"
                  name="name"
                  placeholder="Your Name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full bg-white border border-gray-300 rounded-[14px] px-4 py-4 text-base placeholder:text-gray-400 focus:outline-none focus:border-[#B11226] transition-colors"
                  required
                />
              </div>
              
              <div>
                <input
                  type="email"
                  name="email"
                  placeholder="Your Email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full bg-white border border-gray-300 rounded-[14px] px-4 py-4 text-base placeholder:text-gray-400 focus:outline-none focus:border-[#B11226] transition-colors"
                  required
                />
              </div>
              
              <div>
                <textarea
                  name="message"
                  placeholder="Your Message"
                  rows={5}
                  maxLength={500}
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  className="w-full bg-white border border-gray-300 rounded-[14px] px-4 py-4 text-base placeholder:text-gray-400 focus:outline-none focus:border-[#B11226] transition-colors resize-none"
                  required
                ></textarea>
                <p className="mt-1 text-xs text-gray-500 text-right">
                  {formData.message.length}/500 characters
                </p>
              </div>

              {submitStatus === 'success' && (
                <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-lg text-sm">
                  Thank you! Your message has been sent successfully.
                </div>
              )}

              {submitStatus === 'error' && (
                <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg text-sm">
                  Please fill in all fields correctly.
                </div>
              )}

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-[#B11226] text-white text-base font-semibold py-[18px] rounded-full hover:bg-[#8F0E1E] transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
              >
                {isSubmitting ? 'Sending...' : 'Send Message'}
              </button>
            </form>
          </div>

          {/* Right Column - Info */}
          <div className="space-y-8 lg:pt-16">
            <div>
              <div className="text-xs font-medium text-[#666666] uppercase tracking-wider mb-2">
                Email
              </div>
              <a 
                href="mailto:info@precious-leader-trade.com" 
                className="text-[17px] text-[#111111] hover:text-[#B11226] transition-colors"
              >
                info@precious-leader-trade.com
              </a>
            </div>

            <div>
              <div className="text-xs font-medium text-[#666666] uppercase tracking-wider mb-2">
                Address
              </div>
              <div className="text-[17px] text-[#111111] leading-relaxed">
                3/F, Laford Centre, Unit 304-7<br />
                838 Lai Chi Kok Rd, Cheung Sha Wan<br />
                Kowloon, Hong Kong
              </div>
            </div>

            <div className="bg-[#FAFAFA] rounded-lg p-4">
              <p className="text-sm text-[#666666]">
                We reply within 24–48 hours.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}